/* eslint-disable @atlaskit/design-system/ensure-design-token-usage, no-unused-vars, @atlaskit/design-system/no-unsafe-design-token-usage */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useState } from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { cssMap, jsx } from '@atlaskit/css';

import { expValEquals } from '@confluence/feature-experiments';

import type { CoverPictureId } from '../pageCoverPictureTypes.ts';
import {
	COVER_IMAGE_FIXED_HEIGHT,
	CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE,
} from '../pageCoverPictureConstants.ts';

import { ImageWrapper } from './ImageWrapper.tsx';

type UnsplashLoadingDivProps = {
	hasUnsplashImageLoaded: boolean;
	dynamicCoverImageHeight?: number;
};

const styles = cssMap({
	base: {
		display: 'flex',
		width: '100%',
		position: 'absolute',
	},
	loading: {
		position: 'relative',
		width: '100%',
		backgroundColor: 'var(--ds-background-accent-gray-subtlest)',
	},
	noOpacity: {
		opacity: 0,
	},
	opacity: {
		opacity: 1,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsplashLoadingDivStyled = styled.div<
	UnsplashLoadingDivProps & { smartSpaceOverviewHeight?: number }
>({
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props) =>
		props.smartSpaceOverviewHeight
			? `${props.smartSpaceOverviewHeight}px`
			: `${props.dynamicCoverImageHeight}px`,
	width: '100%',
	background: token('color.background.accent.gray.subtlest'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: (props) => (props.hasUnsplashImageLoaded ? 0 : 1),
	borderRadius: 'unset',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsplashWrapperStyled = styled.div<{
	hasUnsplashImageLoaded: boolean;
	dynamicCoverImageHeight?: number;
	isFixedWidthImageOption?: boolean;
	smartSpaceOverviewHeight?: number;
}>({
	display: 'flex',
	width: '100%',
	position: 'absolute',
	borderRadius: 'unset',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minHeight: (props) => {
		if (props.smartSpaceOverviewHeight) {
			return `${props.smartSpaceOverviewHeight}px`;
		}
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		if (expValEquals('confluence_ttvc_fix_dynamic_cover_image_unsplash', 'isEnabled', true)) {
			if (props.isFixedWidthImageOption) {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				return `${COVER_IMAGE_FIXED_HEIGHT}px`;
			}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
			return `${CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE}px`;
		}
		return `${props.dynamicCoverImageHeight}px`;
	},
});

export type UnsplashCoverPictureProps = {
	coverPicture: React.MutableRefObject<{ draft: CoverPictureId | null }>;
	dynamicCoverImageHeight?: number;
	isFixedWidthImageOption?: boolean;
	smartSpaceOverviewHeight?: number;
	coverPictureDataPosition?: number;
	coverPictureDataId?: string;
	isRoundedCorners?: boolean;
};

const getMinHeightStyle = (
	isFixedWidthImageOption?: boolean,
	smartSpaceOverviewHeight?: number,
	dynamicCoverImageHeight?: number,
) => {
	if (smartSpaceOverviewHeight) {
		return `${smartSpaceOverviewHeight}px`;
	}
	if (expValEquals('confluence_ttvc_fix_dynamic_cover_image_unsplash', 'isEnabled', true)) {
		if (isFixedWidthImageOption) {
			return `${COVER_IMAGE_FIXED_HEIGHT}px`;
		}
		return `${CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE}px`;
	}
	return dynamicCoverImageHeight ? `${dynamicCoverImageHeight}px` : undefined;
};

const UnsplashWrapper = ({
	hasUnsplashImageLoaded,
	dynamicCoverImageHeight,
	isFixedWidthImageOption,
	smartSpaceOverviewHeight,
	children,
}: {
	hasUnsplashImageLoaded: boolean;
	dynamicCoverImageHeight?: number;
	isFixedWidthImageOption?: boolean;
	smartSpaceOverviewHeight?: number;
	children: React.ReactNode;
}) => {
	if (expValEquals('confluence_page_cover_picture_css_migration', 'isEnabled', true)) {
		return (
			<div
				css={styles.base}
				style={{
					minHeight: getMinHeightStyle(
						isFixedWidthImageOption,
						smartSpaceOverviewHeight,
						dynamicCoverImageHeight,
					),
				}}
				data-testid="unsplash-wrapper-css" // TODO: rename this to unsplash-wrapper when cleaning up confluence_page_cover_picture_css_migration
			>
				{children}
			</div>
		);
	}

	return (
		<UnsplashWrapperStyled
			hasUnsplashImageLoaded={hasUnsplashImageLoaded}
			dynamicCoverImageHeight={dynamicCoverImageHeight}
			isFixedWidthImageOption={isFixedWidthImageOption}
			smartSpaceOverviewHeight={smartSpaceOverviewHeight}
			data-testid="unsplash-wrapper"
		>
			{children}
		</UnsplashWrapperStyled>
	);
};

const UnsplashLoadingDiv = ({
	hasUnsplashImageLoaded,
	dynamicCoverImageHeight,
	smartSpaceOverviewHeight,
}: {
	hasUnsplashImageLoaded: boolean;
	dynamicCoverImageHeight?: number;
	smartSpaceOverviewHeight?: number;
}) => {
	if (expValEquals('confluence_page_cover_picture_css_migration', 'isEnabled', true)) {
		return (
			<div
				css={[styles.loading, hasUnsplashImageLoaded ? styles.noOpacity : styles.opacity]}
				style={{
					height: smartSpaceOverviewHeight
						? `${smartSpaceOverviewHeight}px`
						: dynamicCoverImageHeight
							? `${dynamicCoverImageHeight}px`
							: undefined,
				}}
				data-testid="unsplash-loading-div-css" // TODO: rename this to unsplash-loading-div when cleaning up confluence_page_cover_picture_css_migration
			/>
		);
	}

	return (
		<UnsplashLoadingDivStyled
			hasUnsplashImageLoaded={hasUnsplashImageLoaded}
			dynamicCoverImageHeight={dynamicCoverImageHeight}
			smartSpaceOverviewHeight={smartSpaceOverviewHeight}
			data-testid="unsplash-loading-div"
		/>
	);
};

export const UnsplashCoverPicture = ({
	coverPicture,
	dynamicCoverImageHeight,
	isFixedWidthImageOption,
	smartSpaceOverviewHeight,
	coverPictureDataPosition,
	coverPictureDataId,
	isRoundedCorners,
}: UnsplashCoverPictureProps) => {
	const [hasUnsplashImageLoaded, setHasUnsplashImageLoaded] = useState<boolean>(false);

	return (
		<UnsplashWrapper
			hasUnsplashImageLoaded={hasUnsplashImageLoaded}
			dynamicCoverImageHeight={dynamicCoverImageHeight}
			isFixedWidthImageOption={isFixedWidthImageOption}
			smartSpaceOverviewHeight={smartSpaceOverviewHeight}
			data-testid="unsplash-wrapper"
		>
			{!expValEquals('confluence_ttvc_fix_dynamic_cover_image_unsplash', 'isEnabled', true) && (
				<UnsplashLoadingDiv
					hasUnsplashImageLoaded={hasUnsplashImageLoaded}
					dynamicCoverImageHeight={dynamicCoverImageHeight}
					smartSpaceOverviewHeight={smartSpaceOverviewHeight}
					data-testid="unsplash-loading-div"
				/>
			)}
			<ImageWrapper
				data-testid="unsplash-cover-picture"
				id="actual-cover-picture"
				data-vc="unsplash-cover-picture-editor"
				src={coverPicture.current.draft?.value.id ?? coverPictureDataId}
				coverPicturePosition={
					coverPicture.current.draft?.value.position ?? coverPictureDataPosition
				}
				onLoad={() => {
					setHasUnsplashImageLoaded(true);
				}}
				isFixedWidthImageOption={isFixedWidthImageOption}
				smartSpaceOverviewHeight={smartSpaceOverviewHeight}
				isRoundedCorners={isRoundedCorners}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
				className="cover-picture-wrapper-dangerously-set-inner-html"
			/>
		</UnsplashWrapper>
	);
};
